import React, { useState, useEffect, useCallback, useRef } from 'react';
import FteTrackingItem from '../../Domain/OutsourcedUserFte/FteTrackingItem';
import OutsourcedUserFteService from '../../Services/OutsourcedUserFte/OutsourcedUserFteService';
import ShownPanelsEnum from '../../Enums/ShownPanelsEnum';
import CrudTypesEnum from '../../Enums/CrudTypesEnum';
import FigmaButtonRow from '../../Elements/FigmaButtonRow';
import ButtonTypeEnum from '../../Enums/ButtonTypeEnum';
import ButtonColorsEnum from '../../Enums/ButtonColorsEnum';
import IconPositionEnum from '../../Enums/IconPositionEnum';
import GeneralEditWizzardBase from '../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import EditOutsourcedUserFteWindowDescription from '../User/UserAdmin/EditOutsourcedUserFteWindowDescription';
import EditFteTrackingWindowDescription from './EditFteTrackingWindowDescription';
import EditReverseIobWindowDescription, { ReverseIobItem } from './EditReverseIobWindowDescription';
import EditChangeStatusWindowDescription, { ChangeStatusItem } from './EditChangeStatusWindowDescription';
import ErrorMessage from '../../Elements/ErrorMessage';
import TabOfDataTable from '../../Elements/DataTable/TabOfDataTable';
import FteTrackingSearchParams from './FteTrackingSearchParams';
import FteToolbar from './FteToolbar';
import Loading from '../../Elements/loading';
import useFteWarnings from './useFteWarnings';
import useMonthSelector from './useMonthSelector';
import { handleIobQueuedResponse, handleReverseIobResponse } from './fteTrackingUtils';
import UserService from '../../Services/userService';

function FteTrackingAdmin() {
  const [shownPanel, setShownPanel] = useState(ShownPanelsEnum.None);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [itemList, setItemList] = useState([]);
  const [, setSelectionTick] = useState(0);

  const { warnings, refreshWarnings } = useFteWarnings();
  const { searchItem, setSearchItem, changeMonth, refreshSearchItem } = useMonthSelector();

  const createFunction = () => {
    const newItem = new FteTrackingItem();
    newItem.UserId = '';
    newItem.Status = 'Open';
    newItem._showUserSelector = true;
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const lastDate = new Date(y, now.getMonth() + 1, 0).getDate();
    newItem.StartDateUtc = `${y}-${m}-01`;
    newItem.EndDateUtc = `${y}-${m}-${String(lastDate).padStart(2, '0')}`;
    setSelectedItem(newItem);
    setShownPanel(ShownPanelsEnum.AddNewItem);
  };

  const refresh = useCallback(
    (doRefresh) => {
      setShownPanel(ShownPanelsEnum.None);
      setSelectedItem(null);
      if (doRefresh) {
        refreshSearchItem();
        refreshWarnings();
      }
    },
    [refreshSearchItem, refreshWarnings]
  );

  const createReverseIob = useCallback((entries) => {
    // Validate: all entries must be invoiced
    const nonInvoiced = entries.filter((item) => !item.IoblineItemId);
    if (nonInvoiced.length > 0) {
      ErrorMessage.SetErrorMessage('All selected entries must be invoiced (have an IOB) to create a reverse IOB.');
      return;
    }

    // Validate: all entries must have the same fieldwork office
    const fieldworkOfficeIds = [...new Set(entries.map((item) => item.FieldworkOfficeId))];
    if (fieldworkOfficeIds.length > 1) {
      ErrorMessage.SetErrorMessage('All selected entries must have the same fieldwork office.');
      return;
    }

    // Validate: all entries must have the same billing-from office (Hiring Office override, or home office)
    const fromOfficeIds = [...new Set(entries.map((item) => item.HiringOfficeId || item.UserOfficeId))];
    if (fromOfficeIds.length > 1 || !fromOfficeIds[0]) {
      ErrorMessage.SetErrorMessage('All selected entries must have the same billing office (hiring or home office).');
      return;
    }

    // Reversed: ToOffice = the original billing-from office (Hiring Office override, or home office)
    const toOfficeId = fromOfficeIds[0];
    const toOfficeName = entries[0].HiringOfficeName || entries[0].UserOfficeName || `Office #${toOfficeId}`;
    const fteEntryIds = entries.map((item) => item.OutsourcedUserFteid);

    const reverseItem = new ReverseIobItem(fteEntryIds, toOfficeId, toOfficeName);
    setSelectedItem(reverseItem);
    setShownPanel(ShownPanelsEnum.AddNewItem + '_reverse');
  }, []);

  useEffect(() => {
    FteTrackingItem.editFunction = (id, item) => {
      UserService.GetUserList().then((users) => {
        const user = users.find((u) => String(u.userId) === String(item.UserId));
        const userOfficeId = user ? user.officeId : null;
        setSelectedItem({
          ...item,
          _userOfficeId: userOfficeId,
          HiringOfficeId: item.HiringOfficeId || userOfficeId,
        });
        setShownPanel(ShownPanelsEnum.EditItem);
      });
    };
    FteTrackingItem.deleteFunction = (item) => {
      ErrorMessage.SetApprove('Are you sure you want to delete this FTE entry?', () => {
        OutsourcedUserFteService.Delete(item.OutsourcedUserFteid)
          .then((response) => {
            if (response && response.error) {
              ErrorMessage.SetErrorMessage(response.error);
            } else {
              ErrorMessage.SetNotification('FTE entry deleted successfully.');
              refresh(true);
            }
          })
          .catch(() => {
            ErrorMessage.SetErrorMessage('Error! ErrorCode: FTE02. Failed to delete FTE entry. Please notify IT!');
          });
      });
    };
    FteTrackingItem.lockFunction = (item) => {
      ErrorMessage.SetApprove(
        'Are you sure you want to lock this FTE entry? This action is final and irreversible.',
        () => {
          OutsourcedUserFteService.ChangeStatus({ FteEntryIds: [item.OutsourcedUserFteid], TargetStatus: 'Locked' })
            .then((response) => {
              if (response && response.error) {
                ErrorMessage.SetErrorMessage(response.error);
              } else {
                ErrorMessage.SetNotification('FTE entry locked successfully.');
                refresh(true);
              }
            })
            .catch(() => {
              ErrorMessage.SetErrorMessage('Error! ErrorCode: FTE03. Failed to lock FTE entry. Please notify IT!');
            });
        }
      );
    };
    FteTrackingItem.reverseIobFunction = (item) => {
      createReverseIob([item]);
    };
  }, [refresh, createReverseIob]);

  // Wrap clickOnSelect/clickOnGlobalSelect once after TabOfDataTable mounts to track selection changes
  const selectionWrapped = useRef(false);
  useEffect(() => {
    if (selectionWrapped.current) return;
    selectionWrapped.current = true;
    const boundClickOnSelect = FteTrackingItem.clickOnSelect;
    FteTrackingItem.clickOnSelect = (item, selected) => {
      boundClickOnSelect(item, selected);
      setSelectionTick((t) => t + 1);
    };
    const boundClickOnGlobalSelect = FteTrackingItem.clickOnGlobalSelect;
    FteTrackingItem.clickOnGlobalSelect = (item, selected) => {
      boundClickOnGlobalSelect(item, selected);
      setSelectionTick((t) => t + 1);
    };
  });

  const lockEntries = () => {
    const { Year, Month } = searchItem;
    const selectedEntries = itemList.filter((item) => item.Selected);
    const hasSelection = selectedEntries.length > 0;

    const warning = ' This action is final and irreversible, make sure you really want to do it.';
    const confirmMessage = hasSelection
      ? `Are you sure you want to lock ${selectedEntries.length} selected FTE entry/entries?${warning}`
      : `Are you sure you want to lock all open/confirmed FTE entries for ${Year}/${String(Month).padStart(
          2,
          '0'
        )}?${warning}`;

    ErrorMessage.SetApprove(confirmMessage, () => {
      setLoading(true);
      const promise = hasSelection
        ? OutsourcedUserFteService.ChangeStatus({
            FteEntryIds: selectedEntries.map((item) => item.OutsourcedUserFteid),
            TargetStatus: 'Locked',
          })
        : OutsourcedUserFteService.ChangeStatus({ Year, Month, TargetStatus: 'Locked' });

      promise
        .then((response) => {
          if (response && response.error) {
            ErrorMessage.SetErrorMessage(response.error);
            setLoading(false);
          } else {
            ErrorMessage.SetNotification(response || 'Entries locked successfully.');
            setLoading(false);
            refresh(true);
          }
        })
        .catch(() => {
          ErrorMessage.SetErrorMessage('Error! ErrorCode: FTE04. Failed to lock entries. Please notify IT!');
          setLoading(false);
        });
    });
  };

  const generateIobByMonth = () => {
    const { Year, Month } = searchItem;
    const selectedEntries = itemList.filter((item) => item.Selected);
    const hasSelection = selectedEntries.length > 0;

    const request = hasSelection
      ? { FteEntryIds: selectedEntries.map((item) => item.OutsourcedUserFteid) }
      : { Year, Month };

    const confirmMessage = hasSelection
      ? `Generate IOB invoices for ${selectedEntries.length} selected FTE entry/entries?`
      : `Generate IOB invoices for all locked FTE entries in ${Year}/${String(Month).padStart(2, '0')}?`;

    ErrorMessage.SetApprove(confirmMessage, () => {
      setLoading(true);
      OutsourcedUserFteService.GenerateIob(request)
        .then((response) => {
          setLoading(false);
          if (response && response.error) {
            ErrorMessage.SetErrorMessage(response.error);
            return;
          }
          // CD-5466: the request is queued, nothing has changed on this page yet — the entries flip to
          // Invoiced when the background generation finishes and reports back in a popup.
          handleIobQueuedResponse(response);
        })
        .catch((error) => {
          ErrorMessage.SetErrorMessage(
            error?.isBusinessError
              ? error.message
              : 'Error! ErrorCode: FTE06. Failed to generate IOB invoices. Please notify IT!'
          );
          setLoading(false);
        });
    });
  };

  const reverseIobFromSelection = () => {
    const selectedEntries = itemList.filter((item) => item.Selected);
    if (selectedEntries.length === 0) {
      ErrorMessage.SetErrorMessage('Please select one or more invoiced FTE entries to create a reverse IOB.');
      return;
    }
    createReverseIob(selectedEntries);
  };

  const openChangeStatusPanel = () => {
    const selected = itemList.filter((item) => item.Selected);
    const hasSelected = selected.length > 0;
    const item = hasSelected
      ? new ChangeStatusItem(
          selected.map((i) => i.OutsourcedUserFteid),
          null,
          null
        )
      : new ChangeStatusItem([], searchItem.Year, searchItem.Month);
    setSelectedItem(item);
    setShownPanel('ChangeStatus');
  };

  const selectedEntries = itemList.filter((item) => item.Selected);
  const selectedCount = selectedEntries.length;
  const hasSelection = selectedCount > 0;
  const allSelectedAreLocked = hasSelection && selectedEntries.every((item) => item.Status === 'Locked');
  const allSelectedAreInvoiced = hasSelection && selectedEntries.every((item) => !!item.IoblineItemId);

  if (loading) return <Loading />;

  return (
    <div className='contentwrapper'>
      <div className='d-flex justify-content-end align-items-start flex-column align-items-end gap-2 mb-3'>
        <FigmaButtonRow
          className={'InnerMenuButtonFigmaList'}
          buttons={[
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Secondary,
              iconPosition: IconPositionEnum.Right,
              onClick: createFunction,
              label: 'Create',
              tooltip: 'Create a new FTE entry',
              roleKey: 'outsourcedfte_edit',
              iconName: 'fa-solid fa-plus-circle',
            },
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Secondary,
              iconPosition: IconPositionEnum.Right,
              onClick: openChangeStatusPanel,
              label: hasSelection ? `Change Status (${selectedCount})` : 'Change Status',
              tooltip: 'Change status of selected or all open/confirmed FTE entries',
              roleKey: 'outsourcedfte_edit',
              iconName: 'fa-solid fa-arrows-rotate',
            },
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Secondary,
              iconPosition: IconPositionEnum.Right,
              onClick: lockEntries,
              label: hasSelection ? `Lock (${selectedCount})` : 'Lock',
              tooltip: 'Lock selected or all open/confirmed FTE entries for the selected month',
              roleKey: 'outsourcedfte_lock',
              iconName: 'fa-solid fa-lock',
            },
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Primary,
              iconPosition: IconPositionEnum.Right,
              onClick: generateIobByMonth,
              label: hasSelection ? `Generate IOB (${selectedCount})` : 'Generate IOB',
              tooltip: 'Generate IOB invoices for selected or all locked FTE entries',
              roleKey: 'outsourcedfte_iobgenerate',
              iconName: 'fa-solid fa-file-invoice-dollar',
              disabled: hasSelection && !allSelectedAreLocked,
            },
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Danger,
              iconPosition: IconPositionEnum.Right,
              onClick: reverseIobFromSelection,
              label: hasSelection ? `Reverse IOB (${selectedCount})` : 'Reverse IOB',
              tooltip: 'Create a reverse IOB for selected invoiced FTE entries',
              roleKey: 'outsourcedfte_iobreverse',
              iconName: 'fa-solid fa-rotate-left',
              disabled: !allSelectedAreInvoiced,
            },
          ]}
        />

        <FteToolbar
          warnings={warnings}
          year={searchItem.Year}
          month={searchItem.Month}
          onChangeMonth={changeMonth}
        />
      </div>
      {shownPanel === 'ChangeStatus' && (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={selectedItem}
          clickOnCancel={() => refresh(false)}
          clickOnSave={() => refresh(true)}
          windowDescription={EditChangeStatusWindowDescription}
          errorCode={'FTE09'}
        />
      )}
      {shownPanel === ShownPanelsEnum.AddNewItem && (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={selectedItem}
          clickOnCancel={() => refresh(false)}
          clickOnSave={() => refresh(true)}
          windowDescription={EditFteTrackingWindowDescription}
          errorCode={'FTE07'}
        />
      )}
      {shownPanel === ShownPanelsEnum.EditItem && (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Update}
          editedItem={selectedItem}
          clickOnCancel={() => refresh(false)}
          clickOnSave={() => refresh(true)}
          windowDescription={EditOutsourcedUserFteWindowDescription}
          errorCode={'FTE05'}
        />
      )}
      {shownPanel === ShownPanelsEnum.AddNewItem + '_reverse' && (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={selectedItem}
          clickOnCancel={() => refresh(false)}
          clickOnSave={(response) => {
            handleReverseIobResponse(response);
            refresh(true);
          }}
          windowDescription={EditReverseIobWindowDescription}
          errorCode={'FTE08'}
        />
      )}
      <TabOfDataTable
        type={FteTrackingItem}
        searchParamsType={FteTrackingSearchParams}
        shownPanel={shownPanel}
        serviceFunction={OutsourcedUserFteService.GetListBySearching}
        convertDBItemsFunction={OutsourcedUserFteService.ConvertFteTrackingItems}
        searchItem={searchItem}
        showSearchStrip={true}
        clickOnStartSearch={(newSearchItem) => {
          newSearchItem.Year = searchItem.Year;
          newSearchItem.Month = searchItem.Month;
          setSearchItem({ ...newSearchItem });
        }}
        orderBy='UserName'
        orderDirection='Asc'
        enableUrlPush={false}
        errorCode='FTE50'
        forceClearCacheOnSearch={true}
        isSelectAllDisabled={true}
        changeOfItemListFunction={setItemList}
      />
    </div>
  );
}

export default FteTrackingAdmin;
